import React, { useState, useEffect, useRef, useMemo } from 'react';
import { newCampaign } from '../../header/HeaderMainPage';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { IoArrowBack } from 'react-icons/io5';
import CampaignSummary from '../managingPage/campaignSummary';
import { HiDotsVertical } from 'react-icons/hi';

/* ------------------------------- Tiny Primitives ------------------------------ */
const StarIcon = ({ filled }) => (
  <svg
    className={`w-5 h-5 ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
    fill="currentColor"
    viewBox="0 0 20 20"
    aria-hidden
  >
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const StatBadge = ({ children, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-700 ring-gray-200',
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    blue: 'bg-blue-50 text-blue-700 ring-blue-200',
    yellow: 'bg-amber-50 text-amber-700 ring-amber-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
  };
  return (
    <span className={`inline-flex items-center justify-center px-2 h-6 rounded-full text-xs font-semibold ring-1 ${tones[tone]}`}>
      {children}
    </span>
  );
};

/* ------------------------------ Legacy/Reserved ------------------------------ */
// Kept for flow parity (not used by the new card UI but retained intentionally)
const columnWidths = {
  checkbox: '50px',
  star: '50px',
  campaign: '300px',
  deliveryDate: '150px',
  coreproduct: '150px',
  actions: '100px',
  value: '100px',
};
const LoadingCards = () => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 w-full shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
        <div className="w-5 h-5 bg-gray-200 rounded-sm animate-pulse"></div>
      </div>

      <div className="mb-2">
        <div className="h-6 bg-gray-200 rounded-md w-4/5 animate-pulse"></div>
      </div>

      <div className="mb-1">
        <div className="h-3 bg-gray-200 rounded w-24 animate-pulse"></div>
      </div>

      <div className="mb-6">
        <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
      </div>

      <div className="flex items-center gap-6 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-3.5 bg-gray-200 rounded w-16 animate-pulse"></div>
          <div className="h-7 w-9 bg-yellow-100 rounded-full animate-pulse"></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3.5 bg-gray-200 rounded w-10 animate-pulse"></div>
          <div className="h-7 w-9 bg-blue-100 rounded-full animate-pulse"></div>
        </div>
      </div>

      <div className="flex items-center gap-6 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-3.5 bg-gray-200 rounded w-12 animate-pulse"></div>
          <div className="h-7 w-9 bg-yellow-100 rounded-full animate-pulse"></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3.5 bg-gray-200 rounded w-12 animate-pulse"></div>
          <div className="h-7 w-9 bg-teal-100 rounded-full animate-pulse"></div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <div className="h-3.5 bg-gray-200 rounded w-16 animate-pulse"></div>
        <div className="h-7 w-9 bg-teal-100 rounded-full animate-pulse"></div>
      </div>

      <div className="h-11 bg-purple-400 rounded-lg animate-pulse"></div>
    </div>
  );
};

const LoadingCardsGrid = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map((_, i) => (
        <LoadingCards key={i} />
      ))}
    </div>
  );
};

/* --------------------------------- Component -------------------------------- */
const MyCampaign = () => {
  const navigate = useNavigate();

  // Core state (unchanged flow)
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [anySelected, setAnySelected] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const roboman_api = process.env.REACT_APP_ROBOMAN_API;
  // const campaignSelectionRef = useRef(null);

  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (campaignSelectionRef.current && !campaignSelectionRef.current.contains(event.target)) {
  //       setOpenDropdown(null);
  //     }
  //   };
  //   const handleScroll = () => setOpenDropdown(null);
  //   document.addEventListener('mousedown', handleClickOutside);
  //   document.addEventListener('wheel', handleScroll);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //     document.removeEventListener('wheel', handleScroll);
  //   };
  // }, []);

  const updateColumnWidth = (column, width) => {
    columnWidths[column] = width;
    setCampaigns([...campaigns]);
  };

  const fetchCampaignDataFromAPI = async (access_token) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${roboman_api}/campaign/personal`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: access_token },
      });
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        return [];
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user campaign data:', error);
      return null;
    }
    finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    const formattedDate = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    return `${time} ${formattedDate}`;
  };

  const fetchCampaignData = async () => {
    const access_token = localStorage.getItem('access_token');
    const userCampaign = await fetchCampaignDataFromAPI(access_token);
    if (userCampaign) {
      const staticCampaigns = userCampaign.map((item) => ({
        id: item.campaign_id,
        name: item.campaign_name,
        deliveryDate: formatDate(item.created_at),
        isStarred: false,
        isSelected: false,
        campaigns_id: item.campaign_id,
        campaigns_info: item.campaign_info,
        email_format_info: item.email_format,
        must_have_info: item.campaign_contact?.campaign_email || '',
        stats: item.stats,
      }));
      setCampaigns(staticCampaigns);
    }
  };

  useEffect(() => {
    fetchCampaignData();
  }, []);

  const handleTabClick = (tab) => setActiveTab(tab);

  const handleStarClick = (id) => {
    setCampaigns(
      campaigns.map((campaign) => (campaign.id === id ? { ...campaign, isStarred: !campaign.isStarred } : campaign))
    );
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setCampaigns(campaigns.map((c) => ({ ...c, isSelected: !selectAll })));
    setAnySelected(!selectAll);
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${roboman_api}/campaign/${id}/delete`, {
        method: 'DELETE',
        headers: { accept: 'application/json', Authorization: localStorage.getItem('access_token') },
      });
      if (!response.ok) {
        console.error('Failed to delete campaign', response);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'There was a problem deleting the campaign. Please try again later.',
          confirmButtonText: 'OK',
        });
        return;
      }
      setCampaigns(campaigns.filter((c) => c.id !== id));
      Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Your campaign has been deleted successfully.', confirmButtonText: 'OK' });
    } catch (error) {
      console.error('Error deleting campaign:', error);
      Swal.fire({ icon: 'error', title: 'Error!', text: 'There was a problem deleting the campaign. Please try again later.', confirmButtonText: 'OK' });
    }
    setOpenDropdown(null);
  };

  const handleDetails = (campaign) => {
    localStorage.setItem('campaign_name', campaign.name);
    localStorage.setItem('campaign_id', campaign.id);
    navigate(`/email-status`);
  };

  const handleCheckboxChange = (id) => {
    setCampaigns(campaigns.map((c) => (c.id === id ? { ...c, isSelected: !c.isSelected } : c)));
    const newAnySelected = campaigns.some((c) => (c.id === id ? !c.isSelected : c.isSelected));
    setAnySelected(newAnySelected);
  };

  const handleDeleteSelected = async () => {
    const campaign_delete_list_id = campaigns.filter((c) => c.isSelected).map((c) => c.id);
    if (campaign_delete_list_id.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No Campaigns Selected', text: 'Please select at least one campaign to delete.' });
      return;
    }
    try {
      Swal.fire({ title: 'Deleting Campaigns...', text: 'Please wait while the selected campaigns are being deleted.', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      for (let i = 0; i < campaign_delete_list_id.length; i++) {
        try {
          const response = await fetch(`${roboman_api}/campaign/${campaign_delete_list_id[i]}/delete`, {
            method: 'DELETE',
            headers: { accept: 'application/json', Authorization: localStorage.getItem('access_token') },
          });
          if (!response.ok) console.error('Failed to delete campaign', response);
        } catch (error) {
          console.error('Error deleting campaign:', error);
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Selected campaigns have been deleted successfully.' });
    } catch (error) {
      console.error('Error during deletion process:', error);
      Swal.fire({ icon: 'error', title: 'Error!', text: 'There was a problem deleting the campaigns. Please try again later.' });
    }
    await fetchCampaignData();
  };

  const handleBack = () => navigate('/dashboard');

  const filteredCampaigns = useMemo(
    () =>
      campaigns
        .filter((c) => (activeTab === 'All' ? true : c.isStarred))
        .filter((c) =>
          (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (c.library && c.library.toLowerCase().includes(searchTerm.toLowerCase()))
        ),
    [campaigns, activeTab, searchTerm]
  );

  // const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const handleDropdownToggle = (campaignId, event) => {
    setOpenDropdown((prev) => (prev === campaignId ? null : campaignId));
  };

  /* ----------------------------------- UI ----------------------------------- */
  return (
    <div className=" min-h-screen ">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 sm:mt-10">
        {/* Page header */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-4 sm:p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={handleBack}
              className="flex-shrink-0 inline-flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
              aria-label="Back"
            >
              <IoArrowBack size={18} />
            </button>
            <div className='min-w-0'>
              <h1 className="text-lg sm:text-xl md:text-2xl font-extrabold font-Inter opacity-1 bg-gradient-to-r from-gray-700 via-blue-700 to-fuchsia-700  bg-clip-text text-transparent tracking-tight">Campaigns</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">Manage and track your outreach campaigns</p>
            </div>
          </div>

          <button
            onClick={() => newCampaign(navigate)}
            className=" w-full sm:w-auto inline-flex items-center justify-center h-10 sm:h-11 px-4 sm:px-5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold shadow-sm hover:from-blue-700 hover:to-indigo-700 transition"
          >
            + New Campaign
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
          {['All', 'Important'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabClick(tab)}
              className={`h-9 px-4 py-2 rounded-full text-sm font-medium transition ${activeTab === tab ? 'bg-violet-600 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-gray-200 rounded-xl p-3.5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 ">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              aria-label="Select all"
            />
            <div className="relative">
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-[320px] pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                aria-label="Search campaigns"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Bulk actions */}
          <div className="flex items-center gap-2">
            <button
              className={`h-9 px-4 rounded-xl text-sm font-semibold transition ${anySelected ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              onClick={handleDeleteSelected}
              disabled={!anySelected}
            >
              Delete Selected
            </button>
          </div>
        </div>

        {/* Cards Grid (Rectangular boxy design) */}
        {isLoading ?
          (
            <LoadingCardsGrid />
          )
          :
          (filteredCampaigns.length === 0 ? (
            <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-10 text-center mt-2 ">
              <div className="mx-auto w-full max-w-md">
                <div className="text-2xl font-semibold text-gray-800 mb-1">No campaigns yet</div>
                <p className="text-gray-500 mb-4">Create a campaign to start your outreach.</p>
                <button
                  onClick={() => newCampaign(navigate)}
                  className="inline-flex items-center justify-center h-8 px-4 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold hover:bg-blue-700"
                >
                  + New Campaign
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5 mt-4">
              {filteredCampaigns.map((campaign) => (
                <section
                  key={campaign.id}
                  className="rounded-xl bg-white border border-gray-200 shadow-sm p-4 hover:shadow-lg hover:border-blue-300 transition-all duration-200 relative group"
                >
                  {/* Header row: select + star + menu */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={campaign.isSelected}
                        onChange={() => handleCheckboxChange(campaign.id)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        aria-label={`Select ${campaign.name}`}
                      />
                      <button onClick={() => handleStarClick(campaign.id)} className="focus:outline-none" aria-label="Star">
                        <StarIcon filled={campaign.isStarred} />
                      </button>
                    </div>
                    <div className='relative'>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDropdownToggle(campaign.id, e)
                        }

                        }
                        className="text-gray-400 hover:text-gray-600 rounded-lg p-1.5 transition opacity-0 group-hover:opacity-100"
                        aria-label="More actions"
                      >
                        <HiDotsVertical className="w-5 h-5" />
                      </button>

                      {/* Floating dropdown menu */}
                      {openDropdown === campaign.id && (
                        <div
                          className=" absolute right-0 top-8 z-50 bg-white shadow-xl rounded-lg border border-gray-200 w-40 overflow-hidden"
                          // style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button onMouseDown={() => setSelectedCampaign(openDropdown)} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                            Summary
                          </button>
                          <button
                            onMouseDown={() => handleDetails(filteredCampaigns.find((c) => c.id === openDropdown))}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                          >
                            Details
                          </button>
                          <button onMouseDown={() => handleDelete(openDropdown)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Title & meta */}
                  <button
                    onClick={() => handleDetails(campaign)}
                    className="text-left w-full mb-3"
                    title={campaign.name}
                  >
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-snug line-clamp-2 hover:text-blue-600 transition-colors break-words">
                      {campaign.name}
                    </h3>
                  </button>

                  {/* Core product */}
                  {campaign.campaigns_info?.core_service && (
                    <div className="mb-3 pb-3 border-b border-gray-100">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Core Product</div>
                      <div
                        className="text-xs sm:text-sm text-gray-800 font-medium truncate"
                        title={campaign.campaigns_info.core_service}
                      >
                        {campaign.campaigns_info.core_service}
                      </div>
                    </div>
                  )}

                  {/* Stats row */}
                  <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-3">
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-1.5 sm:p-2 ring-1 ring-gray-100">
                      <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Prospects</span>
                      <StatBadge tone="gray">{campaign.stats?.prospects_count ?? 0}</StatBadge>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-1.5 sm:p-2 ring-1 ring-gray-100">
                      <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Sent</span>
                      <StatBadge tone="blue">{campaign.stats?.sent?.count ?? 0}</StatBadge>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-1.5 sm:p-2 ring-1 ring-gray-100">
                      <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Open</span>
                      <StatBadge tone="yellow">{campaign.stats?.opened?.count ?? 0}</StatBadge>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-1.5 sm:p-2 ring-1 ring-gray-100">
                      <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Reply</span>
                      <StatBadge tone="green">{campaign.stats?.replied?.count ?? 0}</StatBadge>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-1.5 sm:p-2 ring-1 ring-gray-100 col-span-2">
                      <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Interested</span>
                      <StatBadge tone="green">{campaign.stats?.interested?.count ?? 0}</StatBadge>
                    </div>
                  </div>

                  {/* Footer CTA */}
                  <div className="pt-3 border-t border-gray-100 flex items-center justify-center">
                    <button
                      onClick={() => handleDetails(campaign)}
                      className="w-auto h-9 sm:h-10 rounded-lg p-2 bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:shadow-md text-white text-sm font-semibold transition-all"
                    >
                      View details
                    </button>
                  </div>
                </section>
              ))}
            </div>
          )
          )
        }


        {/* Summary Modal */}
        {selectedCampaign && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <CampaignSummary campaignId={selectedCampaign} onClose={() => setSelectedCampaign(null)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCampaign;
